import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check, BookOpen, Lock, Share2 } from "lucide-react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { ShareDialog } from "@/components/shared/ShareDialog";
import { api, getErrMsg } from "@/lib/api";

export default function Playbooks() {
  const [books, setBooks] = useState([]);
  const [shareBook, setShareBook] = useState(null);

  const load = async () => {
    const { data } = await api.get("/playbooks");
    setBooks(data);
  };

  useEffect(() => { load(); }, []);

  const purchase = async (id) => {
    try {
      await api.post("/playbooks/purchase", { playbook_id: id });
      toast.success("Playbook unlocked", { description: "Simulated purchase — available in your library." });
      load();
    } catch (err) {
      toast.error("Could not purchase", { description: getErrMsg(err) });
    }
  };

  return (
    <DashboardLayout>
      <div className="border-b border-border px-6 md:px-10 py-6">
        <div className="overline gold-text">Frameworks</div>
        <h1 className="font-display font-medium text-3xl md:text-4xl tracking-tight mt-1">Playbooks</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
          Institutional-grade frameworks. Structured from real executed transactions.
        </p>
      </div>

      <div className="px-6 md:px-10 py-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {books.map((b) => (
          <div key={b.id} className="card-institutional p-6 flex flex-col relative" data-testid={`playbook-${b.id}`}>
            <button
              onClick={() => setShareBook(b)}
              className="absolute top-4 right-4 h-8 w-8 rounded-md border border-border hover:border-[hsl(var(--gold))] flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              data-testid={`share-playbook-${b.id}`}
              aria-label={`Share ${b.title}`}
            >
              <Share2 className="h-3.5 w-3.5" />
            </button>
            <BookOpen className="h-6 w-6 gold-text mb-5" strokeWidth={1.5} />
            <div className="overline">{b.category}</div>
            <h3 className="font-display font-medium text-lg mt-2 min-h-[3.5rem] pr-10">{b.title}</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">{b.description}</p>
            <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="font-mono">{b.pages} pp.</span>
              <span>·</span>
              <span className="italic line-clamp-1">"{b.excerpt}"</span>
            </div>
            <div className="divider my-5" />
            <div className="flex items-center justify-between">
              <div className="font-mono text-xl gold-text">${b.price_usd}</div>
              {b.owned ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--status-live))]">
                  <Check className="h-3.5 w-3.5" /> Owned
                </span>
              ) : (
                <button onClick={() => purchase(b.id)} className="btn-primary h-9 px-4 text-xs" data-testid={`buy-playbook-${b.id}`}>
                  Unlock <ArrowRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {shareBook && (
        <ShareDialog
          open={!!shareBook}
          onOpenChange={(v) => !v && setShareBook(null)}
          type="playbook"
          entityId={shareBook.id}
          entityTitle={shareBook.title}
          shareText={`${shareBook.title} — ${shareBook.description}`}
        />
      )}
    </DashboardLayout>
  );
}
